import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { MetadataOption } from "@/hooks/use-itsm-metadata";

interface Props {
  options: MetadataOption[];
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  /** Allow typing a value not in the list (graceful fallback when metadata
   *  is unavailable). Default true. */
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

/** Searchable dropdown fed by provider metadata. `modal` so it works inside
 *  a Dialog. Falls back to a free-typed value when the list can't cover it. */
export function MetadataSelect({
  options,
  value,
  onChange,
  isLoading,
  placeholder = "Select...",
  allowCustom = true,
  disabled,
  className,
  ...aria
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = options.find((o) => o.value === value);
  const label = selected?.label ?? value;

  const showCreate =
    allowCustom &&
    search.trim() &&
    !options.some((o) => o.value.toLowerCase() === search.trim().toLowerCase());

  const sorted = useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label)),
    [options],
  );

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          {...aria}
          className={cn("h-8 w-full justify-between font-normal", className)}
        >
          <span className={cn(!value && "text-muted-foreground", "truncate")}>
            {value ? label : isLoading ? "Loading..." : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[220px] p-0">
        <Command>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Loading..." : "No options found."}
            </CommandEmpty>
            <CommandGroup>
              {sorted.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  onSelect={() => { pick(o.value); }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === o.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{o.label}</span>
                  {o.label !== o.value && (
                    <span className="text-muted-foreground ml-2 truncate text-xs">
                      {o.value}
                    </span>
                  )}
                </CommandItem>
              ))}
              {showCreate && (
                <CommandItem value={`__custom_${search}`} onSelect={() => { pick(search.trim()); }}>
                  Use "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
